// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Import Firebase services needed directly
import { db, storage } from '../firebase';
import { collection, query, where, getDocs, getCountFromServer, Timestamp, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';

// Import date-fns functions
import { format, getMonth } from 'date-fns';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faUserFriends, faLayerGroup,
    faBirthdayCake, faHeart, faMobileAlt, faTimes, faStar,
    faCalendarAlt, faChevronRight, faRocket, faCheckCircle, faArrowRight,
    faBullhorn, faLightbulb, faEnvelope, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';

// Import react-bootstrap
import { Alert } from 'react-bootstrap';

// Import the Auth Context hook and components
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';

import './Dashboard.css';

// --- Event Photo Thumbnail for Dashboard ---
function EventPhoto({ photoPath }) {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        if (!photoPath) return;
        let cancelled = false;
        // Use 200x200 thumbnail — sufficient for 50px display, fast to load
        const lastDot = photoPath.lastIndexOf('.');
        const thumbPath = lastDot !== -1
            ? `${photoPath.substring(0, lastDot)}_200x200.webp`
            : photoPath;
        getDownloadURL(ref(storage, thumbPath))
            .then(downloadUrl => { if (!cancelled) setUrl(downloadUrl); })
            .catch(() => {
                // Fall back to original if resized version not ready yet
                getDownloadURL(ref(storage, photoPath))
                    .then(downloadUrl => { if (!cancelled) setUrl(downloadUrl); })
                    .catch(() => {});
            });
        return () => { cancelled = true; };
    }, [photoPath]);

    if (!url) return null;
    return <img src={url} alt="" className="upcoming-event-photo" />;
}

// --- Helper to format Month/Day verbosely ---
const formatVerboseDate = (monthNumStr, dayNumStr) => {
    const monthNum = parseInt(monthNumStr, 10);
    const dayNum = parseInt(dayNumStr, 10);
    if (isNaN(monthNum) || isNaN(dayNum) || monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
       return `${monthNumStr || '?'}/${dayNumStr || '?'}`;
    }
    try {
        return format(new Date(2000, monthNum - 1, dayNum), 'MMMM d');
    } catch (e) {
        return `${monthNum}/${dayNum}`;
    }
};

// --- Main Component: Dashboard ---
function Dashboard() {
  const { currentUser, userOrgId, loadingAuth, isAdmin, userFidClaim } = useAuth();

  const [counts, setCounts] = useState({ families: 0, members: 0, groups: 0 });
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [showMobileAd, setShowMobileAd] = useState(() => !localStorage.getItem('dismissMobileAd'));
  const [showWelcome, setShowWelcome] = useState(() => !localStorage.getItem('dismissWelcome'));
  const [showGetStarted, setShowGetStarted] = useState(() => !localStorage.getItem('dismissGetStarted'));
  const [showNextSteps, setShowNextSteps] = useState(() => !localStorage.getItem('dismissNextSteps'));
  const [pendingJoinCount, setPendingJoinCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loadingAuth || !userOrgId) {
        if (!loadingAuth && currentUser && !userOrgId) {
            setError("Your email address was not found. Please contact your church office to have your email added to the directory.");
            setLoading(false);
        }
        if (!loadingAuth) {
           setLoading(false);
        }
        return;
    }

    setLoading(true);
    setError(null);
    let isMounted = true;

    const fetchData = async () => {
      try {
        const currentMonth = getMonth(new Date()) + 1;
        const currentMonthString = String(currentMonth);

        const fetchCounts = async () => {
            const familiesQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId));
            const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId));
            const groupsQuery = query(collection(db, 'group'), where('orgId', '==', userOrgId));
            const [fSnap, mSnap, gSnap] = await Promise.all([getDocs(familiesQuery), getDocs(membersQuery), getDocs(groupsQuery)]);
            return { families: fSnap.size, members: mSnap.size, groups: gSnap.size };
        };

        const fetchMobileStatus = async () => {
            if (localStorage.getItem('dismissMobileAd')) return false;
            if (!currentUser) return true;
            try {
                // Check if user has any tokens (indicating they have the mobile app)
                const tokensQuery = query(collection(db, 'users', currentUser.uid, 'tokens'));
                const tokensSnap = await getDocs(tokensQuery);
                // Hide banner if user has tokens registered
                return tokensSnap.empty;
            } catch(err) {
                console.warn("Dashboard: Could not check user tokens for mobile status", err);
            }
            return true;
        };

        const fetchBirthdays = async () => {
            const today = new Date();
            const currentDay = today.getDate();
            const bdayQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId), where('birthdayMonth', '==', currentMonthString));
            const bdaySnap = await getDocs(bdayQuery);
            const birthdayList = bdaySnap.docs.map(d => {
                 const data = d.data();
                 const monthStr = data.birthdayMonth;
                 const dayStr = data.birthdayDay;
                 const dayOfMonth = parseInt(dayStr, 10) || 0;
                 return {
                    id: d.id,
                    familyId: data.familyId || '',
                    name: data.name || '',
                    surname: data.surname || '',
                    verboseBirthday: formatVerboseDate(monthStr, dayStr),
                    dayOfMonth: dayOfMonth,
                    isToday: dayOfMonth === currentDay,
                    photo: data.photo,
                    imageUpdatedAt: data.imageUpdatedAt
                };
             });
             birthdayList.sort((a, b) => a.dayOfMonth - b.dayOfMonth);
             return birthdayList;
        };

        const fetchAnniversaries = async () => {
            const today = new Date();
            const currentDay = today.getDate();
            const annivQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId), where('anniversaryMonth', '==', currentMonthString));
            const annivSnap = await getDocs(annivQuery);
            const anniversaryListPromises = annivSnap.docs.map(async (d) => {
                const familyData = d.data();
                const monthStr = familyData.anniversaryMonth;
                const dayStr = familyData.anniversaryDay;
                const dayOfMonth = parseInt(dayStr, 10) || 0;
                let photoURL = '/assets/blank.png';
                if (familyData.photo) {
                    try {
                        photoURL = familyData.photo.startsWith('http') ? familyData.photo : await getDownloadURL(ref(storage, familyData.photo));
                    }
                    catch (e) { if (e.code !== 'storage/object-not-found') { console.error(`Error getting anniversary photo ${familyData.photo}:`, e); } }
                }
                return { id: d.id, surname: familyData.surname || '', verboseAnniversary: formatVerboseDate(monthStr, dayStr), dayOfMonth: dayOfMonth, isToday: dayOfMonth === currentDay, photoURL: photoURL };
            });
            const anniversaryList = await Promise.all(anniversaryListPromises);
            anniversaryList.sort((a, b) => a.dayOfMonth - b.dayOfMonth);
            return anniversaryList;
        };

        const fetchUpcomingEvents = async () => {
            const eventsQuery = query(
                collection(db, 'events'),
                where('orgId', '==', userOrgId),
                orderBy('dateTime', 'asc')
            );
            const eventsSnap = await getDocs(eventsQuery);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return eventsSnap.docs
                .map(d => ({ ...d.data(), id: d.id }))
                .filter(event => {
                    const eventDate = event.dateTime?.toDate?.() || new Date(event.dateTime);
                    return eventDate >= today;
                })
                .slice(0, 5);
        };

        const fetchPendingJoinCount = async () => {
            if (!isAdmin) return 0;
            try {
                const joinQuery = query(
                    collection(db, 'joinRequests'),
                    where('orgId', '==', userOrgId),
                    where('status', '==', 'pending')
                );
                const snap = await getCountFromServer(joinQuery);
                return snap.data().count;
            } catch (e) {
                console.warn("Dashboard: Could not fetch pending join count", e);
                return 0;
            }
        };

        const [countResults, mobileStatus, birthdayResults, anniversaryResults, eventsResults, joinCount] = await Promise.all([
            fetchCounts(),
            fetchMobileStatus(),
            fetchBirthdays(),
            fetchAnniversaries(),
            fetchUpcomingEvents(),
            fetchPendingJoinCount()
        ]);

        if (isMounted) {
            setCounts(countResults);
            setShowMobileAd(mobileStatus);
            setBirthdays(birthdayResults);
            setAnniversaries(anniversaryResults);
            setUpcomingEvents(eventsResults);
            setPendingJoinCount(joinCount);
            setError(null);
        }

      } catch (err) {
        console.error("Dashboard: Error fetching data:", err);
        if (isMounted) { setError(err.message || "Failed to load dashboard data."); }
      } finally {
        if (isMounted) { setLoading(false); }
      }
    };

    fetchData();

    return () => { isMounted = false; };
  }, [userOrgId, loadingAuth, currentUser]);

  // Get current month name for headers
  const currentMonthName = format(new Date(), 'MMMM');

  // Loading state
  if (loadingAuth || loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading">
          <div className="skeleton-header"></div>
          <div className="skeleton-stats"></div>
          <div className="skeleton-card"></div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!currentUser) {
    return (
      <div className="dashboard-page">
        <div className="container mt-3">
          <Alert variant="warning">
            Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view the dashboard.
          </Alert>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-page">
        <div className="container mt-3">
          <Alert variant="danger">Error: {error}</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Page Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="dashboard-header-content">
            <h1 className="dashboard-title">Dashboard</h1>
            {userFidClaim && (
              <Link to={`/families/${userFidClaim}`} className="dashboard-family-btn">
                <FontAwesomeIcon icon={faUsers} /> My Family
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        {/* Stats Grid */}
        <div className="stats-grid">
          <Link to="/families" className="stat-card stat-card-clickable">
            <div className="stat-icon stat-icon-families">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{counts.families}</div>
              <div className="stat-label">Families</div>
            </div>
          </Link>

          <div className="stat-card">
            <div className="stat-icon stat-icon-members">
              <FontAwesomeIcon icon={faUserFriends} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{counts.members}</div>
              <div className="stat-label">Members</div>
            </div>
          </div>

          <Link to="/groups" className="stat-card stat-card-clickable">
            <div className="stat-icon stat-icon-groups">
              <FontAwesomeIcon icon={faLayerGroup} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{counts.groups}</div>
              <div className="stat-label">Groups</div>
            </div>
          </Link>
        </div>

        {/* Pending Join Requests Badge (Admin only) */}
        {isAdmin && pendingJoinCount > 0 && (
          <Link to="/admin/join-requests" className="stat-card stat-card-clickable" style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
            border: '1px solid #a5d6a7', padding: '0.75rem 1rem', marginBottom: '1rem',
            borderRadius: '10px', textDecoration: 'none', color: '#2e7d32'
          }}>
            <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: '1.2rem' }} />
            <span style={{ fontWeight: 600 }}>
              {pendingJoinCount} pending join request{pendingJoinCount !== 1 ? 's' : ''}
            </span>
            <FontAwesomeIcon icon={faChevronRight} style={{ marginLeft: 'auto', opacity: 0.6 }} />
          </Link>
        )}

        {/* Admin Onboarding: Get Started + Next Steps */}
        {isAdmin && currentUser?.metadata?.creationTime && (() => {
          const isWithinOneYear = Date.now() - new Date(currentUser.metadata.creationTime).getTime() < 365 * 24 * 60 * 60 * 1000;
          const gettingStartedDone = counts.families > 0 && counts.members > 0;
          const displayGetStarted = showGetStarted && !gettingStartedDone;
          const displayNextSteps = showNextSteps && isWithinOneYear;

          if (!displayGetStarted && !displayNextSteps) return null;

          return (
            <div className={displayGetStarted && displayNextSteps ? 'onboarding-grid' : ''}>
              {/* Get Started Card - until families + members added */}
              {displayGetStarted && (
                <div className="getting-started-card">
                  <div className="getting-started-header">
                    <div className="getting-started-icon">
                      <FontAwesomeIcon icon={faRocket} />
                    </div>
                    <div>
                      <h2 className="getting-started-title">Get Started</h2>
                      <p className="getting-started-subtitle">Set up your church directory.</p>
                    </div>
                    <button
                      className="onboarding-dismiss"
                      onClick={() => { localStorage.setItem('dismissGetStarted', 'true'); setShowGetStarted(false); }}
                      title="Dismiss"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="getting-started-body">
                    {/* Step 1: Set up automated emails */}
                    <Link to="/communications?tab=automations" className="getting-started-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <span className="step-label">Set up automated emails</span>
                        <span className="step-description">Send birthday wishes, anniversary greetings, and welcome messages automatically.</span>
                      </div>
                      <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                    </Link>

                    {/* Step 2: Add families */}
                    {counts.families === 0 ? (
                      <div className="getting-started-step-group">
                        <Link to="/import-directory" className="getting-started-step">
                          <div className="step-number">2</div>
                          <div className="step-content">
                            <span className="step-label">Import your directory</span>
                            <span className="step-description">Upload a CSV or paste a list of names to add families in bulk.</span>
                          </div>
                          <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                        </Link>
                        <Link to="/families/add" className="getting-started-step getting-started-step-alt">
                          <div className="step-number-alt">or</div>
                          <div className="step-content">
                            <span className="step-label">Add families one by one</span>
                            <span className="step-description">Create a family record with surname, address, and photo.</span>
                          </div>
                          <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                        </Link>
                      </div>
                    ) : (
                      <div className="getting-started-step getting-started-step-complete">
                        <div className="step-number step-number-complete">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                        <div className="step-content">
                          <span className="step-label">Add your first family</span>
                          <span className="step-description">Done — {counts.families} {counts.families === 1 ? 'family' : 'families'} added.</span>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Add family members */}
                    {counts.members === 0 && counts.families > 0 ? (
                      <Link to="/families" className="getting-started-step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                          <span className="step-label">Add family members</span>
                          <span className="step-description">Open a family and add members — names, birthdays, and photos.</span>
                        </div>
                        <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                      </Link>
                    ) : counts.members > 0 ? (
                      <div className="getting-started-step getting-started-step-complete">
                        <div className="step-number step-number-complete">
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                        <div className="step-content">
                          <span className="step-label">Add family members</span>
                          <span className="step-description">Done — {counts.members} {counts.members === 1 ? 'member' : 'members'} added.</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Next Steps Card - shown for first year */}
              {displayNextSteps && (
                <div className="getting-started-card next-steps-card">
                  <div className="getting-started-header next-steps-header">
                    <div className="getting-started-icon next-steps-icon">
                      <FontAwesomeIcon icon={faLightbulb} />
                    </div>
                    <div>
                      <h2 className="getting-started-title">Next Steps</h2>
                      <p className="getting-started-subtitle">Explore these features when you're ready.</p>
                    </div>
                    <button
                      className="onboarding-dismiss"
                      onClick={() => { localStorage.setItem('dismissNextSteps', 'true'); setShowNextSteps(false); }}
                      title="Dismiss"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="getting-started-body">
                    <Link to="/groups" className="getting-started-step">
                      <div className="step-number next-step-number"><FontAwesomeIcon icon={faLayerGroup} /></div>
                      <div className="step-content">
                        <span className="step-label">Create a group</span>
                        <span className="step-description">Organize members into teams, committees, or ministries.</span>
                      </div>
                      <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                    </Link>
                    <Link to="/events" className="getting-started-step">
                      <div className="step-number next-step-number"><FontAwesomeIcon icon={faCalendarAlt} /></div>
                      <div className="step-content">
                        <span className="step-label">Create an event</span>
                        <span className="step-description">Keep your congregation informed about upcoming activities.</span>
                      </div>
                      <FontAwesomeIcon icon={faArrowRight} className="step-arrow" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Welcome Card - non-admin users within first 30 days */}
        {!isAdmin && showWelcome && currentUser?.metadata?.creationTime &&
          (Date.now() - new Date(currentUser.metadata.creationTime).getTime() < 30 * 24 * 60 * 60 * 1000) && (
          <div className="welcome-card">
            <button
              className="banner-dismiss welcome-dismiss"
              onClick={() => { localStorage.setItem('dismissWelcome', 'true'); setShowWelcome(false); }}
              title="Dismiss"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div className="welcome-header">
              <div className="welcome-icon">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div>
                <h2 className="welcome-title">Welcome!</h2>
                <p className="welcome-subtitle">Here's what you can do in Church Aware.</p>
              </div>
            </div>
            <div className="welcome-body">
              <ul className="welcome-list">
                <li><strong>Browse the directory</strong> — find families and members in your church</li>
                <li><strong>View events</strong> — see upcoming activities and celebrations</li>
                <li><strong>Edit your family</strong> — update your own contact info, photos, and details</li>
              </ul>
              {userFidClaim && (
                <Link to={`/families/${userFidClaim}`} className="welcome-family-link">
                  View My Family <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Mobile App Banner */}
        {showMobileAd && (
          <div className="mobile-app-banner">
            <div className="mobile-app-content">
              <div className="mobile-app-icon">
                <FontAwesomeIcon icon={faMobileAlt} />
              </div>
              <div className="mobile-app-text">
                <h4>Get the Church Aware Mobile App</h4>
                <p>Access your directory on the go with push notifications for birthdays and anniversaries.</p>
              </div>
            </div>
            <div className="mobile-app-links">
              <a
                href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store-link"
              >
                <FontAwesomeIcon icon={faApple} /> App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store-link"
              >
                <FontAwesomeIcon icon={faAndroid} /> Google Play
              </a>
            </div>
            <button
              className="banner-dismiss"
              onClick={() => { localStorage.setItem('dismissMobileAd', 'true'); setShowMobileAd(false); }}
              title="Dismiss"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Upcoming Events Section */}
        {upcomingEvents.length > 0 && (
          <div className="upcoming-events-section">
            <div className="celebration-card">
              <div className="celebration-header celebration-header-events">
                <div className="celebration-icon celebration-icon-events">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <h2 className="celebration-title">Upcoming Events</h2>
                <Link to="/events" className="events-view-all">
                  View All <FontAwesomeIcon icon={faChevronRight} />
                </Link>
              </div>
              <div className="celebration-body">
                <div className="upcoming-events-list">
                  {upcomingEvents.map(event => {
                    const eventDate = event.dateTime?.toDate?.() || new Date(event.dateTime);
                    const monthName = format(eventDate, 'MMM').toUpperCase();
                    const dayNum = format(eventDate, 'd');
                    const timeStr = format(eventDate, 'h:mm a');
                    const fullDateStr = format(eventDate, 'EEEE, MMMM d, yyyy h:mm a');
                    return (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className="upcoming-event-item"
                      >
                        {event.photo ? (
                          <div className="upcoming-event-photo-wrapper">
                            <EventPhoto photoPath={event.photo} />
                          </div>
                        ) : (
                          <div className="upcoming-event-date-badge">
                            <span className="event-month">{monthName}</span>
                            <span className="event-day">{dayNum}</span>
                          </div>
                        )}
                        <div className="upcoming-event-info">
                          <span className="event-title">{event.title}</span>
                          <span className="event-time">{event.photo ? fullDateStr : timeStr}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Celebrations Grid */}
        <div className="celebrations-grid">
          {/* Birthdays Card */}
          <div className="celebration-card">
            <div className="celebration-header celebration-header-birthday">
              <div className="celebration-icon celebration-icon-birthday">
                <FontAwesomeIcon icon={faBirthdayCake} />
              </div>
              <h2 className="celebration-title">Birthdays in {currentMonthName}</h2>
            </div>
            <div className="celebration-body">
              {birthdays.length > 0 ? (
                <div className="celebration-list">
                  {birthdays.map(member => {
                    const photoCacheKey = member.imageUpdatedAt instanceof Timestamp
                      ? member.imageUpdatedAt.toMillis().toString()
                      : (member.imageUpdatedAt || member.id);
                    return (
                      <Link
                        key={member.id}
                        to={`/families/${member.familyId}/member/${member.id}`}
                        className={`celebration-item ${member.isToday ? 'celebration-today' : ''}`}
                      >
                        <div className="celebration-photo-wrapper">
                          <MemberPhotoAvatar
                            src={member.photo}
                            alt={`${member.name} ${member.surname}`}
                            size={60}
                            cacheKey={photoCacheKey}
                          />
                          {member.isToday && (
                            <span className="today-badge">
                              <FontAwesomeIcon icon={faStar} />
                            </span>
                          )}
                        </div>
                        <span className="celebration-name">{member.name}</span>
                        <span className="celebration-date">
                          {member.isToday ? 'Today!' : member.verboseBirthday}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="celebration-empty">
                  <div className="celebration-empty-icon">
                    <FontAwesomeIcon icon={faBirthdayCake} />
                  </div>
                  <p>No birthdays this month</p>
                </div>
              )}
            </div>
          </div>

          {/* Anniversaries Card */}
          <div className="celebration-card">
            <div className="celebration-header celebration-header-anniversary">
              <div className="celebration-icon celebration-icon-anniversary">
                <FontAwesomeIcon icon={faHeart} />
              </div>
              <h2 className="celebration-title">Anniversaries in {currentMonthName}</h2>
            </div>
            <div className="celebration-body">
              {anniversaries.length > 0 ? (
                <div className="celebration-list">
                  {anniversaries.map(family => (
                    <Link
                      key={family.id}
                      to={`/families/${family.id}`}
                      className={`celebration-item ${family.isToday ? 'celebration-today' : ''}`}
                    >
                      <div className="celebration-photo-wrapper">
                        <img
                          className="celebration-photo"
                          src={family.photoURL}
                          alt={`${family.surname} Family`}
                          onError={(e) => { e.target.onerror = null; e.target.src='/assets/blank.png'; }}
                        />
                        {family.isToday && (
                          <span className="today-badge today-badge-anniversary">
                            <FontAwesomeIcon icon={faStar} />
                          </span>
                        )}
                      </div>
                      <span className="celebration-name">{family.surname}</span>
                      <span className="celebration-date">
                        {family.isToday ? 'Today!' : family.verboseAnniversary}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="celebration-empty">
                  <div className="celebration-empty-icon">
                    <FontAwesomeIcon icon={faHeart} />
                  </div>
                  <p>No anniversaries this month</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
